// src/utils/platform.js
// ============================================================================
// PLATFORM DETECTION & DESKTOP CLIENT API
//
// Detects if the app is running inside the CritCash Desktop Client (Electron)
// and provides a clean API for interacting with native desktop features:
// - Game downloads
// - Game installation
// - Game launching
// - Library management
//
// When NOT in desktop client, these functions gracefully degrade or return null.
// ============================================================================

/**
 * Check if running inside the CritCash Desktop Client
 */
export const isDesktopApp = () => {
  return typeof window !== 'undefined' && 
         window.critcashDesktop?.isDesktopApp === true;
};

/**
 * Check if running on web (browser, not desktop client)
 */
export const isWebApp = () => {
  return typeof window !== 'undefined' && !isDesktopApp();
};

/**
 * Get platform information from desktop client
 */
export const getPlatformInfo = async () => {
  if (!isDesktopApp()) return null;
  try {
    return await window.critcashDesktop.getPlatformInfo();
  } catch (err) {
    console.error('[Platform] Failed to get platform info:', err);
    return null;
  }
};

// ============================================================================
// DOWNLOAD MANAGEMENT
// ============================================================================

/**
 * Download state tracking
 * Stores current download progress for each game
 */
const downloadListeners = new Map();

/**
 * Start downloading a game
 * @param {string} gameId - The game's unique identifier
 * @param {string} downloadUrl - Firebase Storage URL for the game
 * @param {function} onProgress - Callback for progress updates (0-100)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const downloadGame = async (gameId, downloadUrl, onProgress) => {
  if (!isDesktopApp()) {
    return { success: false, error: 'Desktop client required for downloads' };
  }
  
  try {
    // Set up progress listener
    if (onProgress) {
      window.critcashDesktop.download.onProgress((data) => {
        if (data.gameId === gameId) {
          onProgress(data.progress);
        }
      });
      downloadListeners.set(gameId, true);
    }
    
    const result = await window.critcashDesktop.download.start(gameId, downloadUrl);
    
    // Clean up listener
    if (downloadListeners.has(gameId)) {
      window.critcashDesktop.download.removeProgressListener();
      downloadListeners.delete(gameId);
    }
    
    return result;
  } catch (err) {
    console.error('[Platform] Download failed:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Cancel an in-progress download
 * @param {string} gameId - The game's unique identifier
 */
export const cancelDownload = async (gameId) => {
  if (!isDesktopApp()) return { success: false };
  
  try {
    const result = await window.critcashDesktop.download.cancel(gameId);
    
    // Clean up listener
    if (downloadListeners.has(gameId)) {
      window.critcashDesktop.download.removeProgressListener();
      downloadListeners.delete(gameId);
    }
    
    return result;
  } catch (err) {
    console.error('[Platform] Cancel download failed:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// INSTALLATION MANAGEMENT
// ============================================================================

/**
 * Install a downloaded game
 * @param {string} gameId - The game's unique identifier
 */
export const installGame = async (gameId) => {
  if (!isDesktopApp()) {
    return { success: false, error: 'Desktop client required' };
  }
  
  try {
    return await window.critcashDesktop.game.install(gameId);
  } catch (err) {
    console.error('[Platform] Install failed:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Uninstall a game
 * @param {string} gameId - The game's unique identifier
 */
export const uninstallGame = async (gameId) => {
  if (!isDesktopApp()) {
    return { success: false, error: 'Desktop client required' };
  }
  
  try {
    return await window.critcashDesktop.game.uninstall(gameId);
  } catch (err) {
    console.error('[Platform] Uninstall failed:', err);
    return { success: false, error: err.message };
  }
};

// ============================================================================
// GAME LAUNCHING
// ============================================================================

/**
 * Launch an installed game
 * @param {string} gameId - The game's unique identifier
 */
export const launchGame = async (gameId) => {
  if (!isDesktopApp()) {
    return { success: false, error: 'Desktop client required' };
  }
  
  try {
    return await window.critcashDesktop.game.launch(gameId);
  } catch (err) {
    console.error('[Platform] Launch failed:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Check if a game is currently running
 * @param {string} gameId - The game's unique identifier
 */
export const isGameRunning = async (gameId) => {
  if (!isDesktopApp()) return false;
  
  try {
    return await window.critcashDesktop.game.isRunning(gameId);
  } catch (err) {
    console.error('[Platform] isGameRunning check failed:', err);
    return false;
  }
};

// ============================================================================
// LIBRARY MANAGEMENT
// ============================================================================

/**
 * Get all locally installed games
 * @returns {Promise<Object>} Map of gameId -> game info
 */
export const getInstalledGames = async () => {
  if (!isDesktopApp()) return {};
  
  try {
    return await window.critcashDesktop.library.getGames();
  } catch (err) {
    console.error('[Platform] Failed to get installed games:', err);
    return {};
  }
};

/**
 * Get the local install path for a game
 * @param {string} gameId - The game's unique identifier
 */
export const getGamePath = async (gameId) => {
  if (!isDesktopApp()) return null;
  
  try {
    return await window.critcashDesktop.library.getGamePath(gameId);
  } catch (err) {
    console.error('[Platform] Failed to get game path:', err);
    return null;
  }
};

// ============================================================================
// SETTINGS
// ============================================================================

/**
 * Get current game install directory
 */
export const getInstallPath = async () => {
  if (!isDesktopApp()) return null;
  
  try {
    return await window.critcashDesktop.settings.getInstallPath();
  } catch (err) {
    console.error('[Platform] Failed to get install path:', err);
    return null;
  }
};

/**
 * Set new game install directory
 * @param {string} newPath - New installation directory
 */
export const setInstallPath = async (newPath) => {
  if (!isDesktopApp()) return { success: false };
  
  try {
    return await window.critcashDesktop.settings.setInstallPath(newPath);
  } catch (err) {
    console.error('[Platform] Failed to set install path:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Open folder picker to select install directory
 */
export const selectInstallPath = async () => {
  if (!isDesktopApp()) return null;
  
  try {
    return await window.critcashDesktop.settings.selectInstallPath();
  } catch (err) {
    console.error('[Platform] Failed to select install path:', err);
    return null;
  }
};

// ============================================================================
// DEEP LINK HANDLING
// ============================================================================

/**
 * Register a handler for deep links (critcash://...)
 * @param {function} callback - Called with the deep link URL
 */
export const onDeepLink = (callback) => {
  if (!isDesktopApp()) return () => {};
  
  window.critcashDesktop.deepLink.onDeepLink(callback);
  
  // Return cleanup function
  return () => {
    // Note: Cleanup is handled by the desktop client
  };
};

// ============================================================================
// CONVENIENCE API OBJECT
// ============================================================================

/**
 * Desktop client API object for easy access
 * Usage: import { desktop } from './utils/platform';
 *        if (desktop.isAvailable()) { ... }
 */
export const desktop = {
  // Detection
  isAvailable: isDesktopApp,
  isWeb: isWebApp,
  getPlatformInfo,
  
  // Downloads
  downloadGame,
  cancelDownload,
  
  // Installation
  installGame,
  uninstallGame,
  
  // Launching
  launchGame,
  isGameRunning,
  
  // Library
  getInstalledGames,
  getGamePath,
  
  // Settings
  getInstallPath,
  setInstallPath,
  selectInstallPath,
  
  // Events
  onDeepLink,
};

export default desktop;